"use client";
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { cn } from "@/lib/utils";
import { getWebContext } from "@/utils/tagular/helpers";
import { Button } from "@/components/ui/button";
import Icon from "@/components/icons";
import type { EnrichedProgram } from "@/common/course/types";
import { ClientSideNewProductCard } from "@/components/NewProductCard";
import { constructUrl } from "@/utils/functions/constructUrl";
import useProductEvents from "@/hooks/eventing/useProductEvents";
import { useInView } from "react-intersection-observer";
import { DEFAULT_USE_IN_VIEW_OPTIONS } from "@/constants/tagular/main";
import {
  getBrandStringFromPartnerKeys,
  getTagularSubject,
} from "@/utils/tagular/helpers";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import { createCustomDimensions } from "@/utils/tagular/createDimension";
import { getCompletionTimeText } from "@/components/NewProductCard/utils/getCompletionTimeText";
import { getPacingText } from "@/components/NewProductCard/utils/getPacingText";
import { getPageType } from "@/utils/tagular/helpers";
import { isElementHidden } from "@/utils/isElementHidden";

interface IndustryHighlightsCardsSliderProps {
  programs: (EnrichedProgram & {
    _badges?: Array<{ label: string; icon?: string }>;
    _level?: "Introductory" | "Intermediate" | "Advanced";
  })[];
  getUrl: (path?: string) => string | undefined;
  pageContext?: {
    title: string;
    url: string;
    pageType: string;
  } | null;
  includeToComplete?: boolean;
}

// Static product card type configuration - doesn't need to be recreated
const PRODUCT_CARD_TYPE = {
  singleCourseTags: [
    { tags: "CompletionTime" },
    { tags: "Pacing" },
    { tags: "CourseCount" },
  ],
};

// Component to track ProductViewed event for each card
function ProductCardWithTracking({
  program,
  index,
  getUrl,
  pageContext: sharedPageContext,
  includeToComplete = false,
}: {
  program: EnrichedProgram & {
    _badges?: Array<{ label: string; icon?: string }>;
    _level?: "Introductory" | "Intermediate" | "Advanced";
  };
  index: number;
  getUrl: (path?: string) => string | undefined;
  pageContext?: {
    title: string;
    url: string;
    pageType: string;
  } | null;
  includeToComplete?: boolean;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const viewedRef = useRef(false);
  const clickedRef = useRef(false);

  // Use shared pageContext or create fallback
  const pageContext = useMemo(() => {
    if (sharedPageContext) return sharedPageContext;
    if (typeof window === "undefined") return null;
    const pageType = getPageType();
    const finalPageType = pageType === "other" ? "learn-page" : pageType;
    return {
      title: document.title,
      url: window.location.href,
      pageType: finalPageType,
    };
  }, [sharedPageContext]);

  // Merge full Cohesion webContext (anonymous_id, session_id, etc.) with page override.
  // Normalize userAgent: eventing types expect string | undefined, not null.
  const webContext = useMemo(() => {
    const base = getWebContext();
    return pageContext
      ? {
          ...base,
          userAgent: base?.userAgent ?? undefined,
          page: {
            title: pageContext.title,
            url: pageContext.url,
            pageType: pageContext.pageType,
          },
        }
      : undefined;
  }, [pageContext]);

  // Extract partner names from program.partners array (reused for both eventing and card display)
  const partnerNames = useMemo(() => {
    return (
      program.partners
        ?.map((p) => p.name)
        .filter((name): name is string => !!name) || []
    );
  }, [program.partners]);

  const brand = useMemo(
    () => getBrandStringFromPartnerKeys(partnerNames),
    [partnerNames],
  );

  const category = useMemo(() => {
    // If it's a course (no type or no courses array), use "course" as category
    const isCourse =
      !program.type || !program.courses || program.courses.length === 0;
    if (isCourse) {
      return "course";
    }
    // Otherwise, use the subject
    return getTagularSubject(program.subjects);
  }, [program.type, program.courses, program.subjects]);

  // Format duration
  const duration = useMemo(() => {
    const completionText = getCompletionTimeText(
      program.weeksToComplete,
      includeToComplete,
    );
    return completionText || undefined;
  }, [program.weeksToComplete, includeToComplete]);

  // Format level
  const level = useMemo(() => {
    return program._level || undefined;
  }, [program._level]);

  // Format flexibility (pacing)
  const flexibility = useMemo(() => {
    return getPacingText(program.pacing) || undefined;
  }, [program.pacing]);

  // Format skills/badges
  const skills = useMemo(() => {
    if (program._badges && program._badges.length > 0) {
      return program._badges.map((badge) => badge.label).join(", ");
    }
    return undefined;
  }, [program._badges]);

  // Create custom dimensions
  const customDimensions = useMemo(() => {
    return createCustomDimensions([
      ["duration", duration],
      ["level", level],
      ["flexibility", flexibility],
      ["skill", skills],
    ]);
  }, [duration, level, flexibility, skills]);

  // Create product object for eventing
  const product = useMemo(() => {
    return {
      brand: brand || undefined,
      category: category || undefined,
      formatSubtype: "industry-highlights-recommendations",
      formatType: "recommended",
      location: "results",
      productId: program.uuid,
      name: program.title,
      position: index + 1,
      variant: translateVariant(program.type || "Course"),
    };
  }, [brand, category, program.uuid, program.title, index, program.type]);

  const { productViewed, productClicked, correlationId } = useProductEvents({
    product,
    customDimensions,
  });

  // Use refs to access latest values without recreating the event listener
  const productClickedRef = useRef(productClicked);
  const correlationIdRef = useRef(correlationId);
  const webContextRef = useRef(webContext);

  // Keep refs in sync with latest values
  useEffect(() => {
    productClickedRef.current = productClicked;
    correlationIdRef.current = correlationId;
    webContextRef.current = webContext;
  }, [productClicked, correlationId, webContext]);

  const {
    ref: inViewRef,
    inView,
    entry,
  } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const setRefs = useCallback(
    (node: HTMLDivElement) => {
      cardRef.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  // Optimized: Use useCallback to stabilize the event handler
  // This prevents unnecessary re-executions of the useEffect
  const handleProductViewed = useCallback(() => {
    if (viewedRef.current) {
      return;
    }

    if (entry && !isElementHidden(entry.target as HTMLElement)) {
      productViewed({
        webContext,
      });
      viewedRef.current = true;
    }
  }, [entry, productViewed, webContext]);

  // Fire ProductViewed event when card becomes visible (only once per card)
  // Optimized: Simplified dependencies - only depends on inView and the stable handler
  useEffect(() => {
    if (!inView) {
      return;
    }

    handleProductViewed();
  }, [inView, handleProductViewed]);

  // Optimized: Use useCallback to stabilize the click handler
  // This prevents unnecessary event listener recreation
  const handleClickCapture = useCallback((e: MouseEvent) => {
    // Prevent duplicate events
    if (clickedRef.current) {
      return;
    }

    const target = e.target as HTMLElement;
    const anchor = target.closest("a");

    if (!anchor) {
      return;
    }

    // Mark as clicked immediately to prevent duplicate events
    clickedRef.current = true;
    const outboundUrl = anchor.href;

    // Use refs to access latest values without recreating the listener
    productClickedRef.current({
      correlationId: correlationIdRef.current,
      outboundUrl,
      webContext: webContextRef.current,
    });

    // Mark the event so handleClick knows it was already handled
    (e as any).__productClickedHandled = true;

    // Reset the flag after a short delay
    setTimeout(() => {
      clickedRef.current = false;
    }, 100);
  }, []); // Empty deps - handler is stable, uses refs for latest values

  // Use capture phase to catch the event before it reaches the anchor
  // This replaces the default ProductClicked event from NewProductCard
  // when skipClickEvent={true} is passed to CardWrapper
  useEffect(() => {
    const element = cardRef.current;
    if (!element) return;

    element.addEventListener("click", handleClickCapture, true); // true = capture phase

    return () => {
      element.removeEventListener("click", handleClickCapture, true);
    };
  }, [handleClickCapture]); // Only depends on the stable handler

  const imageUrl = program.cardImageUrl || "";
  const partnerLogoUrl = program.partners?.[0]?.logoUrl || "";
  const courseCount = program.courses?.length || 0;

  const cta = {
    topCtaText: program.type,
    backgroundColor: "putty-200",
  };

  return (
    <div
      ref={setRefs}
      className="w-full [&_a]:no-underline [&_a:hover]:no-underline"
    >
      <ClientSideNewProductCard
        href={constructUrl(program?.marketingUrl, getUrl)}
        imageUrl={imageUrl}
        name={program.title}
        partnerLogoUrl={partnerLogoUrl}
        partnerKey={partnerNames}
        partnerFullName={partnerNames}
        type={program.type || "Course"}
        uuid={program.uuid}
        weeksToComplete={program.weeksToComplete}
        pacing={
          (program.pacing as "self_paced" | "instructor_paced") || "self_paced"
        }
        level={level}
        childCoursesCount={courseCount}
        location="program-carousel"
        index={index}
        productCardType={PRODUCT_CARD_TYPE}
        subjects={program?.subjects ?? []}
        cta={cta}
        skipViewEvent={true}
        skipClickEvent={true}
        includeToComplete={includeToComplete}
      />
    </div>
  );
}

export default function IndustryHighlightsCardsSlider({
  programs,
  getUrl,
  pageContext,
}: IndustryHighlightsCardsSliderProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [slidesPerView, setSlidesPerView] = useState(1);
  const [activeDotIndex, setActiveDotIndex] = useState<number | null>(null);
  const slidesContainerRef = useRef<HTMLDivElement>(null);

  // Detect screen size and update slidesPerView
  useEffect(() => {
    const updateSlidesPerView = () => {
      if (typeof window !== "undefined") {
        setSlidesPerView(window.innerWidth >= 768 ? 2 : 1);
      }
    };

    updateSlidesPerView();
    window.addEventListener("resize", updateSlidesPerView);
    return () => window.removeEventListener("resize", updateSlidesPerView);
  }, []);

  // Limit programs to max 4 cards on mobile
  const visiblePrograms = slidesPerView === 1 ? programs.slice(0, 4) : programs;

  // Determine if slider should be shown based on screen size and number of cards
  const shouldShowSlider =
    slidesPerView === 2
      ? visiblePrograms.length > 2 // Desktop: show slider if more than 2 cards
      : visiblePrograms.length > 1; // Mobile: show slider if more than 1 card

  useEffect(() => {
    if (!slidesContainerRef.current) return;

    // Don't apply transform if slider is not needed
    if (!shouldShowSlider) {
      slidesContainerRef.current.style.transform = "translateX(0%)";
      return;
    }

    // Calculate translateX based on actual card positions
    // Works for both desktop (slidesPerView=2) and mobile (slidesPerView=1)
    const targetCardIndex = currentIndex * slidesPerView;
    const targetCard = slidesContainerRef.current.children[
      targetCardIndex
    ] as HTMLElement;

    if (targetCard) {
      // Move to align the target card with the left edge of the viewport
      const translateX = -targetCard.offsetLeft;

      if (!isInitialized) {
        slidesContainerRef.current.style.transition = "none";
        slidesContainerRef.current.style.transform = `translateX(${translateX}px)`;
        slidesContainerRef.current.offsetHeight;
        slidesContainerRef.current.style.transition = "";
        setIsInitialized(true);
      } else {
        slidesContainerRef.current.style.transform = `translateX(${translateX}px)`;
      }
    }
  }, [
    currentIndex,
    isInitialized,
    slidesPerView,
    shouldShowSlider,
    visiblePrograms.length,
  ]);

  // Reset to first slide when slidesPerView changes
  useEffect(() => {
    setCurrentIndex(0);
    setActiveDotIndex(null);
  }, [slidesPerView]);

  // Handle touch/swipe events
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && canGoNext) {
      goToNext();
    } else if (isRightSwipe && canGoPrevious) {
      goToPrevious();
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  // Calculate number of dots based on screen size and number of cards
  const numberOfDots = useMemo(() => {
    if (slidesPerView === 2) {
      // Desktop: 1 dot = 2 cards, dots to show = number of cards / 2
      return Math.ceil(visiblePrograms.length / 2);
    } else {
      // Mobile: 1 dot = 1 card, dots to show = number of cards, maximum 4 dots
      if (visiblePrograms.length <= 1) return 0;
      return visiblePrograms.length;
    }
  }, [slidesPerView, visiblePrograms.length]);

  // Calculate maxIndex once and reuse
  // For desktop: limit to numberOfDots - 1 since each dot shows 2 cards
  // For mobile: standard calculation
  const maxIndex =
    slidesPerView === 2 && numberOfDots > 0
      ? Math.min(
          numberOfDots - 1,
          Math.max(0, visiblePrograms.length - slidesPerView),
        )
      : Math.max(0, visiblePrograms.length - slidesPerView);

  const goToSlide = (index: number) => {
    setCurrentIndex(Math.min(index, maxIndex));
  };

  const goToNext = () => {
    setActiveDotIndex(null);
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  const goToPrevious = () => {
    setActiveDotIndex(null);
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const canGoNext = currentIndex < maxIndex;
  const canGoPrevious = currentIndex > 0;

  const renderNavigation = () => {
    if (!shouldShowSlider) return null;
    if (numberOfDots === 0) return null;

    // Calculate which card index to show when clicking a dot
    const getSlideIndexForDot = (dotIndex: number) => {
      if (slidesPerView === 1) {
        // Mobile: 1 dot = 1 card, so dotIndex = cardIndex = slideIndex
        return dotIndex;
      }
      // Desktop: dot x shows cards x*2 and x*2+1, so slideIndex = dotIndex
      return dotIndex;
    };

    // Calculate which dot should be active based on currentIndex
    // Use clicked dot if available, otherwise use inverse formula
    const getActiveDotIndex = () => {
      // If a dot was clicked, use it directly
      if (activeDotIndex !== null) {
        return activeDotIndex;
      }

      // Otherwise, calculate using inverse formula (for arrow navigation)
      if (maxIndex === 0) return 0;
      const firstCardIndex = currentIndex * slidesPerView;
      if (slidesPerView === 1) {
        // Mobile: 1 dot = 1 card, so currentIndex = dotIndex
        return firstCardIndex;
      }
      // Desktop: dot x shows cards x*2 and x*2+1, so dotIndex = currentIndex
      return Math.min(currentIndex, numberOfDots - 1);
    };

    const finalActiveDotIndex = getActiveDotIndex();

    return (
      <div className="flex justify-center mt-4">
        <div className="flex items-center bg-white rounded-xl border border-putty-400 px-2 py-2">
          {/* Left arrow */}
          <Button
            variant="ghost"
            size="icon"
            onClick={goToPrevious}
            className="mr-6 h-6 w-6 p-0 text-elm-500 hover:bg-transparent hover:text-elm-500 focus:bg-transparent focus:text-elm-500 focus:outline-none"
            disabled={!canGoPrevious}
          >
            <Icon
              type="chevron-left"
              svgProps={{
                "aria-hidden": "true",
                width: "24",
                height: "24",
              }}
            />
            <span className="sr-only">Previous slide</span>
          </Button>

          {/* Dots */}
          <div className="flex space-x-6">
            {Array.from({ length: numberOfDots }).map((_, dotIndex) => (
              <button
                key={dotIndex}
                onClick={() => {
                  setActiveDotIndex(dotIndex);
                  goToSlide(getSlideIndexForDot(dotIndex));
                }}
                className={cn(
                  "w-3.5 h-3.5 rounded-full transition-all duration-200",
                  dotIndex === finalActiveDotIndex
                    ? "bg-brand-500"
                    : "bg-secondary-500 hover:bg-secondary-400",
                )}
                aria-label={`Go to slide group ${dotIndex + 1}`}
              />
            ))}
          </div>

          {/* Right arrow */}
          <Button
            variant="ghost"
            size="icon"
            onClick={goToNext}
            className="ml-6 h-6 w-6 p-0 text-elm-500 hover:bg-transparent hover:text-elm-500 focus:bg-transparent focus:text-elm-500 focus:outline-none"
            disabled={!canGoNext}
          >
            <Icon
              type="chevron-right"
              svgProps={{
                "aria-hidden": "true",
                width: "24",
                height: "24",
              }}
            />
            <span className="sr-only">Next slide</span>
          </Button>
        </div>
      </div>
    );
  };

  if (programs.length === 0) return null;

  return (
    <>
      {/* Slider container */}
      <div
        className={cn(
          "relative w-full",
          shouldShowSlider ? "overflow-hidden" : "",
        )}
        onTouchStart={shouldShowSlider ? handleTouchStart : undefined}
        onTouchMove={shouldShowSlider ? handleTouchMove : undefined}
        onTouchEnd={shouldShowSlider ? handleTouchEnd : undefined}
      >
        <div
          ref={slidesContainerRef}
          className={cn(
            "flex gap-6 justify-start",
            shouldShowSlider
              ? "transition-transform duration-300 ease-in-out"
              : "",
          )}
        >
          {visiblePrograms.map((program, index) => (
            <div
              key={program.uuid}
              className="flex-shrink-0"
              style={{
                width:
                  slidesPerView === 2 ? "calc((100% - 1.5rem) / 2)" : "100%",
              }}
            >
              <ProductCardWithTracking
                program={program}
                index={index}
                getUrl={getUrl}
                pageContext={pageContext}
                includeToComplete={false}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      {renderNavigation()}
    </>
  );
}
